import {
  activeSessionIdAtom,
  inFlightRecordingSessionIdAtom,
} from '@/atoms/liveTranscriptionAtom';
import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useSessionRecordingStore } from '@heidi/capture';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';

/**
 * Lock Tidy Up while a recording is live or paused: the backend reads a
 * just-started session as empty and would sweep it. Covers this tab's live
 * capture (the record button's atom, and the capture store for starts that skip
 * the button), a paused capture the user navigated away from, offline capture,
 * Chronicle, and the open session's server status. No Redux timer (ratchet).
 */
export const useTidyUpRecordingLock = () => {
  const hasActiveSession = useAtomValue(activeSessionIdAtom) !== null;
  const isCapturing = useSessionRecordingStore((s) => s.status === 'RECORDING');
  const inFlightSessionId = useAtomValue(inFlightRecordingSessionIdAtom);
  const { data: inFlightRecordingStatus } = useQuerySessionById({
    sessionId: inFlightSessionId ?? '',
    select: (session) => session?.recording_status,
  });
  const hasOfflineCapture = useAtomValue(offlineActiveRecordingIdAtom) !== null;
  const isClinicalRecordingActive = useIsClinicalRecordingActive(false);
  const intl = useIntl();
  return {
    isLocked:
      hasActiveSession ||
      isCapturing ||
      inFlightRecordingStatus === RecordingStatus.PAUSED ||
      hasOfflineCapture ||
      isClinicalRecordingActive,
    lockedReason: intl.formatMessage({
      defaultMessage: 'Stop the recording to tidy up sessions',
      id: '3f26JWVPTE',
    }),
  };
};
